import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaEdit } from "react-icons/fa";
import { fetchFetchedallplans } from "../../api/customer.api";
import { businessPath } from "../../config/businessMenu";

const CYCLES = [
  { key: "monthly", label: "Monthly", days: 30 },
  { key: "quarterly", label: "Quarterly", days: 90 },
  { key: "halfYearly", label: "Half-Yearly", days: 180 },
  { key: "annually", label: "Annually", days: 365 },
];

const UNLIMITED_FROM = 1000000;

export const planLabel = (plan) => plan?.name || plan?.planName || "Plan";

const planPrice = (plan, cycleKey) => {
  const cycle = plan?.billingCycles?.[cycleKey];
  if (cycle && cycle.inr != null) return cycle.inr;
  return plan?.price ?? 0;
};

const formatInr = (value) =>
  Number.isFinite(Number(value)) ? Number(value).toLocaleString("en-IN") : "—";

const formatLimit = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "string") return value;
  if (value < 0 || value >= UNLIMITED_FROM) return "Unlimited";
  return String(value);
};

const Yes = () => (
  <span className="renew-mark renew-mark-yes" aria-label="Included" />
);

const No = () => (
  <span className="renew-mark renew-mark-no" aria-label="Not included" />
);

const bool = (plan, key) => (plan?.features?.[key] ? <Yes /> : <No />);

const ROWS = [
  {
    label: "Marketing ₹",
    tone: "price",
    render: (p) => p.conversationPricing?.marketing ?? "—",
  },
  {
    label: "Service",
    tone: "price",
    render: (p) => p.conversationPricing?.service ?? "—",
  },
  {
    label: "Utility ₹",
    tone: "price",
    render: (p) => p.conversationPricing?.utility ?? "—",
  },
  {
    label: "Authentication ₹",
    tone: "price",
    render: (p) => p.conversationPricing?.authentication ?? "—",
  },
  {
    label: "Markup Fee percent",
    tone: "plain",
    render: (p) => `${p.markupFeePercent ?? 0}%`,
  },
  {
    label: "Team Members",
    tone: "plain",
    render: (p) => formatLimit(p.limits?.teamMembers),
  },
  {
    label: "Team Roles",
    tone: "plain",
    render: (p) => formatLimit(p.limits?.teamRoles),
  },
  {
    label: "Max Tags",
    tone: "plain",
    render: (p) => formatLimit(p.limits?.maxTags),
  },
  {
    label: "Max Attributes",
    tone: "plain",
    render: (p) => formatLimit(p.limits?.maxAttributes),
  },
  {
    label: "Max Contacts Import",
    tone: "plain",
    render: (p) => {
      const limit = p.limits?.maxContactsImport ?? p.limits?.contactImportLimit;
      if (limit === null || limit === undefined) {
        return p.features?.contactImport ? "Unlimited" : "—";
      }
      return formatLimit(limit);
    },
  },
  {
    label: "Webhook Limit",
    tone: "plain",
    render: (p) => formatLimit(p.limits?.webhookLimit),
  },
  {
    label: "Daily Campaign Messages",
    tone: "plain",
    render: (p) => formatLimit(p.limits?.dailyCampaignMessages),
  },
  {
    label: "Campaign Scheduler",
    tone: "feature",
    render: (p) => bool(p, "campaignScheduler"),
  },
  {
    label: "Carousel Click Tracking",
    tone: "feature",
    render: (p) => bool(p, "carouselClickTracking"),
  },
  {
    label: "CSV Campaign Scheduler",
    tone: "feature",
    render: (p) => bool(p, "csvCampaignScheduler"),
  },
  {
    label: "Campaign Click Tracking",
    tone: "feature",
    render: (p) => bool(p, "campaignClickTracking"),
  },
  {
    label: "Round Robin Assignment",
    tone: "feature",
    render: (p) => bool(p, "roundRobinAssignment"),
  },
  {
    label: "Automated Follow-Ups",
    tone: "feature",
    render: (p) => bool(p, "automatedFollowUps"),
  },
  {
    label: "Free WABA Access",
    tone: "feature",
    render: (p) => bool(p, "freeWabaAccess"),
  },
  {
    label: "Advanced Analytics",
    tone: "feature",
    render: (p) => bool(p, "advancedAnalytics"),
  },
  {
    label: "Super Meta Analytics",
    tone: "feature",
    render: (p) => bool(p, "superMetaAnalytics"),
  },
  {
    label: "CSV Broadcasting",
    tone: "feature",
    render: (p) => bool(p, "csvBroadcasting"),
  },
  {
    label: "SMS Broadcasting",
    tone: "feature",
    render: (p) => bool(p, "smsBroadcasting"),
  },
  {
    label: "Audience Segmentation",
    tone: "feature",
    render: (p) => bool(p, "audienceSegmentation"),
  },
  {
    label: "Live Chat Dashboard",
    tone: "feature",
    render: (p) => bool(p, "liveChatDashboard"),
  },
  {
    label: "Custom Quick Replies",
    tone: "feature",
    render: (p) => bool(p, "customQuickReplies"),
  },
  {
    label: "Notes Feature",
    tone: "feature",
    render: (p) => bool(p, "notesFeature"),
  },
  {
    label: "Agent Transfer & Monitoring",
    tone: "feature",
    render: (p) => bool(p, "agentTransferMonitoring"),
  },
  {
    label: "Contact Import",
    tone: "feature",
    render: (p) => bool(p, "contactImport"),
  },
  {
    label: "Multi-Agent Chat",
    tone: "feature",
    render: (p) => bool(p, "multiAgentChat"),
  },
  {
    label: "Template Library Access",
    tone: "feature",
    render: (p) => bool(p, "templateLibraryAccess"),
  },
  {
    label: "Create Message Templates",
    tone: "feature",
    render: (p) => bool(p, "createMessageTemplates"),
  },
  {
    label: "Google Sheet Integration",
    tone: "feature",
    render: (p) => {
      const quota = p.limits?.googleSheetQuota;
      const enabled =
        Boolean(p.features?.googleSheetIntegration) || Number(quota) > 0;
      if (!enabled) return <No />;
      return (
        <span className="renew-cell-stack">
          <Yes />
          {quota ? (
            <span className="renew-cell-note">
              {formatLimit(quota)}/month charged separately
            </span>
          ) : null}
        </span>
      );
    },
  },
  {
    label: "Chatbot",
    tone: "chatbot",
    render: (p) => {
      const price = p.chatbot?.extraFlowPrice ?? 0;
      if (!price) return <span className="renew-cell-note">Not Included</span>;
      return (
        <span className="renew-cell-note">
          Catalogue + Flow Builder = ₹{formatInr(price)}/month
          {p.chatbot?.chargedSeparately ? (
            <>
              <br />
              Charged Separately
            </>
          ) : null}
        </span>
      );
    },
  },
];

const PlanComparison = ({
  projectId,
  currentPlanName,
  title = "Renew Your Plan",
  onPaymentClick,
  managePlans = false,
  refreshKey = 0,
  onEdit,
}) => {
  const [rawPlans, setRawPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cycle, setCycle] = useState("monthly");
  const [selectedPlan, setSelectedPlan] = useState(null);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchFetchedallplans();
      const list = Array.isArray(res) ? res : res?.data || [];
      setRawPlans(Array.isArray(list) ? list : []);
    } catch (err) {
      setRawPlans([]);
      setError(err.message || "Failed to load plans");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setSelectedPlan(null);
    setCycle("monthly");
    loadPlans();
  }, [loadPlans, refreshKey]);

  const plans = useMemo(() => {
    const list = rawPlans.filter((p) => {
      if (p.isTrialPlan) return false;
      const name = planLabel(p).toUpperCase();
      return name !== "FREE TRIAL PLAN" && !name.includes("FREE TRIAL");
    });
    const priceOf = (plan) => {
      const value = Number(planPrice(plan, cycle));
      return Number.isFinite(value) ? value : 0;
    };
    return [...list].sort((a, b) => priceOf(b) - priceOf(a));
  }, [rawPlans, cycle]);

  const activeCycle = useMemo(
    () => CYCLES.find((item) => item.key === cycle) || CYCLES[0],
    [cycle],
  );

  const step = selectedPlan ? 2 : 1;

  return (
    <>
      {!managePlans && (
        <ol className="renew-steps">
          <li className={step === 1 ? "is-active" : "is-done"}>
            <span className="renew-step-dot">1</span>
            Choose Plan
          </li>
          <li className={step === 2 ? "is-active" : ""}>
            <span className="renew-step-dot">2</span>
            You&apos;re Ready!
          </li>
        </ol>
      )}

      {!managePlans && step === 2 ? (
        <div className="renew-ready">
          <h2>You&apos;re Ready!</h2>
          <p>
            You selected <strong>{planLabel(selectedPlan)}</strong> for{" "}
            <strong>₹{formatInr(planPrice(selectedPlan, cycle))}</strong> (
            {activeCycle.label.toLowerCase()}, {activeCycle.days} days).
          </p>
          <div className="renew-ready-actions">
            <button
              type="button"
              className="biz-btn-outline"
              onClick={() => setSelectedPlan(null)}
            >
              Back to plans
            </button>
            <Link
              to={businessPath(projectId, "payments")}
              className="renew-select-btn"
              onClick={onPaymentClick}
            >
              Proceed to Payment
            </Link>
          </div>
        </div>
      ) : (
        <>
          <div className="renew-cycle-toggle" role="tablist">
            {CYCLES.map((item) => (
              <button
                key={item.key}
                type="button"
                role="tab"
                aria-selected={cycle === item.key}
                className={`renew-cycle-btn${cycle === item.key ? " is-active" : ""}`}
                onClick={() => setCycle(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>

          <h1 className="renew-title">{title}</h1>

          {loading ? (
            <p className="renew-state">Loading plans…</p>
          ) : error ? (
            <p className="renew-state renew-state-error">{error}</p>
          ) : plans.length === 0 ? (
            <p className="renew-state">No plans available right now.</p>
          ) : (
            <div className="renew-table-wrap">
              <table className="renew-table">
                <thead>
                  <tr>
                    <th className="renew-feature-col">Feature</th>
                    {plans.map((plan) => {
                      const name = planLabel(plan);
                      const isCurrent = name === currentPlanName;
                      return (
                        <th key={plan._id || name}>
                          <span className="renew-plan-name">{name}</span>
                          <span className="renew-plan-price">
                            ₹{formatInr(planPrice(plan, cycle))}
                            <sup>+ taxes</sup>
                          </span>
                          <span className="renew-plan-days">
                            {activeCycle.days} days
                          </span>
                          <button
                            type="button"
                            className="renew-select-btn"
                            onClick={() => setSelectedPlan(plan)}
                            disabled={isCurrent}
                          >
                            {isCurrent ? "Current" : "Select"}
                          </button>
                          {managePlans && (
                            <button
                              type="button"
                              className="renew-edit-btn"
                              onClick={() => onEdit?.(plan)}
                            >
                              <FaEdit /> Edit
                            </button>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                <tbody>
                  {ROWS.map((row) => (
                    <tr key={row.label}>
                      <th
                        scope="row"
                        className={`renew-row-label renew-row-${row.tone}`}
                      >
                        {row.label}
                      </th>
                      {plans.map((plan) => (
                        <td key={(plan._id || planLabel(plan)) + row.label}>
                          {row.render(plan)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default PlanComparison;
